/**
 * useTheme Hook
 * Custom hook for accessing theme context in components
 */

import { useContext } from 'react';
import { ThemeContext } from '../contexts/ThemeContext';
import type { ThemeContextValue } from '../themes';

/**
 * useTheme Hook
 * 
 * Provides access to the current theme and theme switching functions.
 * Must be used within a ThemeProvider component.
 * 
 * @returns {ThemeContextValue} The theme context value containing:
 *   - theme: The current theme name ('retro' | 'modern')
 *   - setTheme: Function to set a specific theme
 *   - toggleTheme: Function to toggle between themes
 * 
 * @throws {Error} If used outside of ThemeProvider
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { theme, setTheme, toggleTheme } = useTheme();
 *   
 *   return (
 *     <div>
 *       <p>Current theme: {theme}</p>
 *       <button onClick={toggleTheme}>Toggle Theme</button>
 *       <button onClick={() => setTheme('modern')}>Use Modern Theme</button>
 *     </div>
 *   );
 * }
 * ```
 * 
 * @example
 * ```tsx
 * function ThemeAwareButton() {
 *   const { theme } = useTheme();
 *   
 *   return (
 *     <button className={theme === 'retro' ? 'pixel-button' : 'modern-button'}>
 *       Click me
 *     </button>
 *   );
 * }
 * ```
 */
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  
  if (context === undefined) {
    throw new Error(
      'useTheme must be used within a ThemeProvider. ' +
      'Make sure your component is wrapped with <ThemeProvider>.'
    );
  }
  
  return context;
}

/**
 * Return type for useTheme hook
 * Re-exported for convenience
 */
export type UseThemeReturn = ThemeContextValue;
